import { memo, useEffect, useMemo, useState, type ReactElement } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { renderMermaid } from '../../markdown/mermaid/renderMermaid';
import { hastToReact } from '../../markdown/render/hastToReact';
import { useColorMode } from '../../theme/ThemeProvider';
import type { MermaidBlockProps } from '../../types';

type Status = 'loading' | 'ready' | 'error';

const ZOOM_STEP = 0.2;
const ZOOM_RANGE = { min: 0.4, max: 3 };

/**
 * Renders one Mermaid diagram in isolation.
 *
 * Failures are contained: an invalid definition renders an inline error with the
 * original source, leaving the rest of the document untouched.
 */
export const MermaidBlock = memo(function MermaidBlock({ code }: MermaidBlockProps) {
  const theme = useTheme();
  const { resolvedMode } = useColorMode();

  const [status, setStatus] = useState<Status>('loading');
  const [diagram, setDiagram] = useState<ReactElement | null>(null);
  const [error, setError] = useState<string>('');
  const [zoom, setZoom] = useState(1);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    renderMermaid(code, resolvedMode)
      .then(({ tree }) => {
        if (cancelled) return;
        setDiagram(hastToReact(tree));
        setStatus('ready');
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : String(cause));
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [code, resolvedMode]);

  const surface = useMemo(
    () => ({
      '& svg': {
        maxWidth: '100%',
        height: 'auto',
        display: 'block',
        margin: '0 auto',
      },
    }),
    [],
  );

  if (status === 'error') {
    return (
      <Alert severity="error" variant="outlined" sx={{ my: 2.5 }} data-testid="mermaid-error">
        <AlertTitle sx={{ fontWeight: 700 }}>Error rendering Mermaid diagram</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {error || 'The diagram definition could not be parsed.'}
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.25,
            borderRadius: 1,
            overflowX: 'auto',
            fontSize: '0.78rem',
            backgroundColor: alpha(theme.palette.error.main, 0.06),
          }}
        >
          <code>{code.trim()}</code>
        </Box>
      </Alert>
    );
  }

  return (
    <>
      <Paper
        variant="outlined"
        data-testid="mermaid-diagram"
        role="img"
        aria-label="Mermaid diagram"
        sx={{
          my: 2.5,
          p: 2,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: theme.markdown.surfaceRaised,
          '&:hover .mermaid-actions': { opacity: 1 },
        }}
      >
        <Stack
          direction="row"
          spacing={0.25}
          className="mermaid-actions"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            borderRadius: 2,
            backgroundColor: theme.markdown.glass,
            border: `1px solid ${theme.palette.divider}`,
            opacity: { xs: 1, md: 0 },
            transition: theme.transitions.create('opacity', { duration: 150 }),
          }}
        >
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                size="small"
                aria-label="Zoom out"
                disabled={zoom <= ZOOM_RANGE.min}
                onClick={() => setZoom((value) => Math.max(ZOOM_RANGE.min, value - ZOOM_STEP))}
              >
                <RemoveRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                size="small"
                aria-label="Zoom in"
                disabled={zoom >= ZOOM_RANGE.max}
                onClick={() => setZoom((value) => Math.min(ZOOM_RANGE.max, value + ZOOM_STEP))}
              >
                <AddRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Reset zoom">
            <IconButton size="small" aria-label="Reset zoom" onClick={() => setZoom(1)}>
              <RestartAltRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Expand diagram">
            <IconButton size="small" aria-label="Expand diagram" onClick={() => setExpanded(true)}>
              <OpenInFullRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {status === 'loading' ? (
          <Skeleton variant="rounded" height={180} animation="wave" />
        ) : (
          <Box
            sx={{
              ...surface,
              overflowX: 'auto',
              '& > *': {
                transform: `scale(${zoom})`,
                transformOrigin: 'top center',
                transition: theme.transitions.create('transform', { duration: 160 }),
              },
            }}
          >
            {diagram}
          </Box>
        )}
      </Paper>

      <Dialog open={expanded} onClose={() => setExpanded(false)} maxWidth="xl" fullWidth>
        <DialogContent sx={{ position: 'relative', p: 3 }}>
          <IconButton
            aria-label="Close diagram"
            onClick={() => setExpanded(false)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseRoundedIcon />
          </IconButton>
          <Box sx={{ ...surface, overflow: 'auto', pt: 2 }}>{diagram}</Box>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default MermaidBlock;
